import { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft,
  MapPin,
  Navigation2,
  Loader2,
  CheckCircle2,
  MapPinOff,
  RotateCcw,
  Search,
  PenLine,
  X,
} from 'lucide-react';
import { getLocationDetails } from '../../services/api';
import { CoordalPostcodeResult, GhanaPostCodeResult } from './CopyablePostcodeGrid';
import { formatPostcodeDisplay, formatPostcodeFull } from '../../utils/postcode';
import { requestCurrentPosition } from '../../utils/geolocationRequest';

type Step = 'pick' | 'result';

export type FindPostcodeSnapshot = {
  step: Step;
  location: { lat: number; lng: number } | null;
  result: Awaited<ReturnType<typeof getLocationDetails>> | null;
};

interface FindPostcodePanelProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigateBack?: () => void;
  restoreSnapshot?: FindPostcodeSnapshot | null;
  onRestoreConsumed?: () => void;
  onEnableMapClick: (enabled: boolean) => void;
  selectedLocation?: { lat: number; lng: number } | null;
  onSetLocation?: (location: { lat: number; lng: number }) => void;
  onFlyTo?: (center: { lat: number; lng: number }, zoom: number) => void;
  onPlaceMarker?: (lat: number, lng: number) => void;
  onRemoveMarker?: () => void;
  onOpenPlace?: (placeId: string) => void;
  onSuggestEdit?: (result: NonNullable<Awaited<ReturnType<typeof getLocationDetails>>>) => void;
}

function isInsideGhana(lat: number, lng: number) {
  return lat >= 4.5 && lat <= 11.5 && lng >= -3.5 && lng <= 1.5;
}

export function FindPostcodePanel({
  isOpen,
  onClose,
  onNavigateBack,
  restoreSnapshot,
  onRestoreConsumed,
  onEnableMapClick,
  selectedLocation,
  onSetLocation,
  onFlyTo,
  onPlaceMarker,
  onRemoveMarker,
  onOpenPlace,
  onSuggestEdit,
}: FindPostcodePanelProps) {
  const [step, setStep] = useState<Step>('pick');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<Awaited<ReturnType<typeof getLocationDetails>> | null>(null);
  const [showOutsideGhana, setShowOutsideGhana] = useState(false);
  const prevLocationRef = useRef<{ lat: number; lng: number } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && step === 'pick') {
      onEnableMapClick(true);
    } else {
      onEnableMapClick(false);
    }
    return () => onEnableMapClick(false);
  }, [isOpen, step, onEnableMapClick]);

  useEffect(() => {
    if (isOpen && step === 'pick' && selectedLocation) {
      const prev = prevLocationRef.current;
      if (!prev || prev.lat !== selectedLocation.lat || prev.lng !== selectedLocation.lng) {
        prevLocationRef.current = selectedLocation;
        onFlyTo?.(selectedLocation, 17);
      }
    }
    if (!selectedLocation) {
      prevLocationRef.current = null;
    }
  }, [isOpen, step, selectedLocation, onFlyTo]);

  const prevIsOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      if (!restoreSnapshot) {
        setStep('pick');
        setError(null);
        setResult(null);
        setShowOutsideGhana(false);
      }
    }
    if (!isOpen && prevIsOpenRef.current) {
      onRemoveMarker?.();
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, onRemoveMarker, restoreSnapshot]);

  useEffect(() => {
    if (!isOpen || !restoreSnapshot) return;
    setStep(restoreSnapshot.step);
    setResult(restoreSnapshot.result);
    setError(null);
    setShowOutsideGhana(false);
    if (restoreSnapshot.location) {
      prevLocationRef.current = restoreSnapshot.location;
      onSetLocation?.(restoreSnapshot.location);
      onPlaceMarker?.(restoreSnapshot.location.lat, restoreSnapshot.location.lng);
      onFlyTo?.(restoreSnapshot.location, 17);
    }
    onRestoreConsumed?.();
  }, [isOpen, restoreSnapshot, onRestoreConsumed, onSetLocation, onPlaceMarker, onFlyTo]);

  useEffect(() => {
    if (step === 'result') {
      scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [step]);

  const handleClose = () => {
    onRemoveMarker?.();
    onClose();
  };

  const handleBack = () => {
    if (step === 'result') {
      handleTryAgain();
      return;
    }
    onRemoveMarker?.();
    if (onNavigateBack) {
      onNavigateBack();
    } else {
      onClose();
    }
  };

  const handleUseMyLocation = useCallback(async () => {
    setError(null);
    try {
      const pos = await requestCurrentPosition();
      const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      if (!isInsideGhana(loc.lat, loc.lng)) {
        setShowOutsideGhana(true);
        return;
      }
      onSetLocation?.(loc);
      onPlaceMarker?.(loc.lat, loc.lng);
      onFlyTo?.(loc, 17);
    } catch {
      setError('Unable to get your location. Please enable location services or drop a pin on the map.');
    }
  }, [onSetLocation, onPlaceMarker, onFlyTo]);

  const lookupPostcode = async () => {
    if (!selectedLocation) {
      setError('Tap the map on a building, use your location, or search for your building in the search bar.');
      return;
    }
    if (!isInsideGhana(selectedLocation.lat, selectedLocation.lng)) {
      setShowOutsideGhana(true);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const details = await getLocationDetails(selectedLocation.lat, selectedLocation.lng, {
        mode: 'building',
      });
      setResult(details);
      setStep('result');
    } catch {
      setError('Could not look up a postcode for this location. Try moving the pin onto a building footprint.');
    } finally {
      setLoading(false);
    }
  };

  const handleTryAgain = () => {
    setStep('pick');
    setResult(null);
    setError(null);
  };

  const coordalCode = result?.postcode ? formatPostcodeFull(result.postcode) : '';
  const coordalDisplay = result?.postcode_display || (result?.postcode ? formatPostcodeDisplay(result.postcode) : '');

  if (!isOpen) return null;

  const sheetMaxHeight = step === 'pick' ? 'max-h-[38vh]' : 'max-h-[88vh] min-h-[62vh]';
  const headerTitle = step === 'result' ? 'Your Postcode' : 'Find My Postcode';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 320 }}
        className={`fixed inset-x-0 bottom-0 z-[100] flex flex-col ${sheetMaxHeight} overflow-hidden rounded-t-2xl bg-white dark:bg-gray-900 shadow-2xl dark:shadow-black/50 border-t border-gray-200 dark:border-gray-700 safe-area-bottom`}
      >
        <div className="flex justify-center pt-2 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" aria-hidden />
        </div>

        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 dark:border-gray-800 shrink-0">
          <button
            type="button"
            onClick={handleBack}
            className="p-2 -ml-2 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors"
          >
            <ChevronLeft size={22} className="text-gray-700 dark:text-neutral-300" />
          </button>
          <h2 className="text-base font-semibold text-gray-900 dark:text-neutral-100">{headerTitle}</h2>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 -mr-2 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors"
            aria-label="Close"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3 pb-4">
          {step === 'pick' && (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                Drop a pin on your building or use your current location to look up your Coordal postcode.
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed flex items-start gap-2">
                <Search size={14} className="shrink-0 mt-0.5 text-gray-400" />
                <span>
                  You can also search for your building in the search bar above, open the result, and copy the postcode from place details.
                </span>
              </p>

              {selectedLocation ? (
                <div className="flex items-center gap-2 p-2.5 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl border border-emerald-200 dark:border-emerald-800">
                  <MapPin size={16} className="text-emerald-600 dark:text-emerald-400 shrink-0" />
                  <span className="text-xs text-emerald-700 dark:text-emerald-300 font-medium">
                    Pin placed. Pan the map to adjust if needed.
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2 p-2.5 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
                  <MapPinOff size={16} className="text-amber-600 dark:text-amber-400 shrink-0" />
                  <span className="text-xs text-amber-700 dark:text-amber-300">
                    Tap the map on a building near you.
                  </span>
                </div>
              )}

              <button
                type="button"
                onClick={handleUseMyLocation}
                className="w-full flex items-center justify-center gap-2 p-2.5 rounded-xl bg-gray-100 dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                <Navigation2 size={16} />
                Use my current location
              </button>

              {error && <p className="text-xs text-red-500 dark:text-red-400">{error}</p>}

              <button
                type="button"
                onClick={lookupPostcode}
                disabled={!selectedLocation || loading}
                className="w-full py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-cyan-500 disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Looking up postcode...
                  </>
                ) : (
                  'Find postcode'
                )}
              </button>
            </>
          )}

          {step === 'result' && result && (
            <>
              {result.postcode ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 size={20} className="text-emerald-500 shrink-0" />
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">Postcode found for this location</p>
                  </div>

                  <CoordalPostcodeResult
                    fullCode={coordalCode}
                    shortCode={coordalDisplay || coordalCode}
                  />

                  {result.ghana_post_gps && (
                    <GhanaPostCodeResult code={result.ghana_post_gps} />
                  )}
                </div>
              ) : (
                <div className="rounded-2xl border border-amber-200 dark:border-amber-800 p-4 bg-amber-50 dark:bg-amber-900/20">
                  <p className="text-sm font-semibold text-amber-800 dark:text-amber-200 mb-1">No building postcode nearby</p>
                  <p className="text-xs text-amber-700 dark:text-amber-300 leading-relaxed">
                    Move the pin onto a building footprint, or contribute your address if it is missing from the map.
                  </p>
                </div>
              )}

              <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 space-y-1">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Address</p>
                <p className="text-sm text-gray-900 dark:text-white">{result.address}</p>
                {(result.district || result.region) && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {[result.district, result.region].filter(Boolean).join(', ')}
                  </p>
                )}
              </div>

              {result.place_id?.startsWith('loc_') && onSuggestEdit && (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                    Help improve Coordal Maps by adding missing details for this building.
                  </p>
                  <button
                    type="button"
                    onClick={() => onSuggestEdit(result)}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-2xl border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-all text-sm font-medium"
                  >
                    <PenLine size={15} />
                    Add Missing Details
                  </button>
                </div>
              )}

              <div className="flex flex-col gap-2">
                {result.place_id && onOpenPlace && (
                  <button
                    type="button"
                    onClick={() => {
                      onOpenPlace(result.place_id!);
                      handleClose();
                    }}
                    className="w-full py-3 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-cyan-500 hover:opacity-90 transition-opacity"
                  >
                    View place details
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleTryAgain}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  <RotateCcw size={16} />
                  Try another location
                </button>
              </div>
            </>
          )}
        </div>

        {showOutsideGhana && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40">
            <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-900 p-5 shadow-xl">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Outside Ghana</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Postcode lookup only works within Ghana. Place your pin on a building in Ghana instead.
              </p>
              <button
                type="button"
                onClick={() => setShowOutsideGhana(false)}
                className="w-full py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-cyan-500"
              >
                OK
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
